"use client"

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { usePathname } from "next/navigation"
import { cn } from "@/lib/utils"
import { isHiddenChromeRoute } from "@/lib/routing/hidden-route-prefixes"
import { useFabAutoCorner, syncFabReserveAttr } from "@/hooks/use-fab-auto-corner"
import { useFabBarLift } from "@/hooks/use-fab-bar-lift"
import { fabCornerTransformFor, useSidebarInset } from "@/hooks/use-fab-sidebar-inset"
import { useUIStore } from "@/stores/ui-store"
import { countBlockingOverlays } from "./blocking-overlays"

/**
 * The FAB stack's portal target. Mounted once per admin layout (never on
 * public pages). Renders a `<div id="fab-stack-root">` into document.body
 * positioned via `transform: translate(...)` so corner swaps animate
 * smoothly instead of teleporting (CSS can't transition between numeric
 * and `auto` for left/right, which is what the previous Tailwind class
 * swap was doing).
 *
 * Auto-relocate: routes containing /new/, /create/, /edit/ AND any state
 * with an open Radix dialog/sheet/drawer move the FAB to the bottom corner at
 * the inline start (bottom-left in English, bottom-right in Arabic) so the
 * primary action area at the inline end stays free. The MutationObserver
 * below pushes/pops the Zustand modal counter as portal nodes appear /
 * disappear, so the relocate is automatic for every modal — no per-modal
 * wiring required.
 *
 * Action bars: a page whose primary actions run along the bottom edge marks
 * itself `data-fab-actions` (the survey builder's `data-survey-step-bar` says
 * the same), and at a bottom corner the stack rests one action band higher
 * (fabCornerTransformFor), so it never covers that page's Back / Save / Publish; a bar the page marks on the element itself is
 * measured, and the stack keeps a gap above it wherever it is on screen (use-fab-bar-lift.ts).
 * The corner is kept, and a creation route does not move it aside (hooks/use-fab-auto-corner.ts).
 *
 * Single-FAB design: container is 48×48 (just the Heart). It does not
 * render any visual decoration of its own — it's purely a portal target.
 *
 * Not drawn on a hidden-chrome route (the console, the portal, sign-in …):
 * its one guest, the feedback FAB, never renders there, and the empty box —
 * fixed, z-9999, taking clicks — would sit over whatever owns that corner
 * (the console assistant's launcher). The effects below still run there, so
 * the tables' corner clearance and the modal count stay in step.
 */
export function FabStackHost() {
  const pathname = usePathname()
  const { effectiveCorner, direction, liftAboveActions } = useFabAutoCorner()
  // A corner on the sidebar's side is measured from the edge of the page
  // content, so the launcher never sits on the account switcher (#970 item 9).
  const sidebarInset = useSidebarInset(pathname)
  // How far the launcher must rise to clear the action bar the page has ON SCREEN (null: no bar to measure, the fixed band applies).
  const barLift = useFabBarLift(effectiveCorner, direction, sidebarInset)
  const [mounted, setMounted] = useState(false)
  const [widgetOpen, setWidgetOpen] = useState(false)

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => setMounted(true), [])
  /* eslint-enable react-hooks/set-state-in-effect */

  // #204 / #230 — mirror the FAB's table-overlap side onto <body> so the
  // pure-CSS safe-area rules (globals.css: `body[data-fab-reserve="right"|
  // "left"] [data-fab-safe-area] [data-fab-footer]`) give up that physical end
  // of a data table's pagination bar while the FAB rests at the bottom corner
  // at the inline end, over the pagination footer + Actions column.
  // Only once mounted: the render that hydrates the server's markup still has
  // the server's view (default corner, no page direction), and until the host
  // knows better the server's empty value stands, which globals.css already
  // pads for the default corner. So a hard load never flips the reserve twice.
  useEffect(() => {
    if (!mounted) return
    syncFabReserveAttr(effectiveCorner, direction)
  }, [mounted, effectiveCorner, direction])

  // Hide the stack while a feedback / AI-chat panel is active — the panel
  // lives near the same corner and the FABs were bleeding into it.
  useEffect(() => {
    const onOpen = () => setWidgetOpen(true)
    const onClose = () => setWidgetOpen(false)
    window.addEventListener("fab-widget:open", onOpen)
    window.addEventListener("fab-widget:close", onClose)
    return () => {
      window.removeEventListener("fab-widget:open", onOpen)
      window.removeEventListener("fab-widget:close", onClose)
    }
  }, [])

  // ── Auto-detect modal/dialog/sheet open state via MutationObserver ──
  //
  // Radix Dialog, Sheet, Drawer, AlertDialog and Popover all stamp
  // `[data-state="open"]` on their portal root when visible. We watch
  // document.body for portal nodes carrying that attribute and keep the
  // Zustand counter in sync. This avoids touching every modal usage site
  // (~40 in this app) — the counter is the source of truth and the
  // observer is just one writer.
  //
  // Excludes: collapsibles, accordions, hover-cards, tooltips — those
  // also use `data-state="open"` but don't block the primary action area.
  // We scope to elements whose `data-slot` is dialog-related, OR that
  // carry a Radix portal wrapper attribute. Falls back to `[role="dialog"]`.
  useEffect(() => {
    if (typeof window === "undefined") return
    const ui = useUIStore.getState()

    // Popovers (filter dropdowns, comboboxes, date pickers) carry role="dialog"
    // too and are left out: see blocking-overlays.ts.
    let lastCount = 0
    const sync = () => {
      const next = countBlockingOverlays(document)
      if (next === lastCount) return
      const delta = next - lastCount
      lastCount = next
      // Fire deltas onto the Zustand counter so the count survives even
      // if the FAB unmounts mid-modal (re-mount picks up the stored count).
      if (delta > 0) {
        for (let i = 0; i < delta; i++) ui.pushModal()
      } else {
        for (let i = 0; i < -delta; i++) ui.popModal()
      }
    }

    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    })
    return () => {
      observer.disconnect()
      // Drain any remaining count on unmount so a remount starts clean.
      for (let i = 0; i < lastCount; i++) ui.popModal()
    }
  }, [])

  if (!mounted || isHiddenChromeRoute(pathname ?? "")) return null

  return createPortal(
    <div
      id="fab-stack-root"
      data-fab-corner={effectiveCorner}
      className={cn(
        // Anchor at (top:0,left:0), translate to the effective corner.
        // The transform animates between corners; opacity handles the
        // dismiss-during-widget-open behaviour.
        "group/fab-stack fixed top-0 left-0 z-[9999] w-12 h-12 will-change-transform",
        // Beautiful ease-out-expo slide between corners; fast start, soft
        // landing. motion-reduce keeps the snap for users who opted out.
        "transition-[transform,opacity] duration-500 ease-[cubic-bezier(.16,1,.3,1)] motion-reduce:transition-none",
        widgetOpen
          ? "opacity-0 pointer-events-none"
          : "opacity-100 pointer-events-auto",
      )}
      style={{ transform: fabCornerTransformFor(effectiveCorner, sidebarInset, direction, liftAboveActions, barLift) }}
    />,
    document.body,
  )
}
